// src/components/PrayerWalls.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Heart, MessageCircle, Search, Send, Bookmark, X, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ---------------- API base + helpers ---------------- */
const RAW_BASE = (import.meta.env.VITE_API_BASE ?? "/api").trim();
const API_BASE = RAW_BASE.replace(/\/$/, "");
const apiURL = (path) => `${API_BASE}${path.startsWith("/") ? path : "/" + path}`;

async function safeJson(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    const text = await res.text();
    throw new Error(`Expected JSON, got ${ct || "unknown"}\n${text.slice(0, 400)}`);
  }
  return res.json();
}

async function request(path, { method = "GET", body, headers = {}, allow401 = false } = {}) {
  const url = apiURL(path);
  const res = await fetch(url, {
    method,
    credentials: "include",
    cache: "no-store",
    headers: { "Content-Type": "application/json", ...headers },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    if (allow401 && (res.status === 401 || res.status === 403)) return null;
    const ct = res.headers.get("content-type") || "";
    const bodyText = ct.includes("application/json")
      ? JSON.stringify(await res.json().catch(() => ({})))
      : await res.text();
    const err = new Error(`HTTP ${res.status} ${res.statusText} at ${url}\n${String(bodyText).slice(0, 400)}`);
    err.status = res.status;
    try { err.payload = JSON.parse(bodyText); } catch { err.payload = bodyText; }
    throw err;
  }
  return safeJson(res);
}

/* ---------------- small utils ---------------- */
const usePageLogger = (data) => { useEffect(() => { console.log("Page logged:", data); }, []); };
const logPrayer = (title, content, category) => { console.log("Prayer logged:", { title, content, category }); };

const Toast = ({ message }) => (
  <div className="fixed top-20 right-4 md:right-6 bg-white shadow-lg rounded-lg px-4 py-3 border-l-4 border-green-500 z-50 animate-slide-in max-w-sm">
    <div className="flex items-center gap-2">
      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
      <span className="text-gray-800 font-medium text-sm md:text-base">{message}</span>
    </div>
  </div>
);

const categories = [
  "All","Healing","Provision","Family","Career","Relationship","Financial","Spiritual","Guidance","Thanksgiving","Other",
];

const categoryColors = {
  Healing: "bg-red-100 text-red-800",
  Family: "bg-green-100 text-green-800",
  Career: "bg-blue-100 text-blue-800",
  Relationship: "bg-purple-100 text-purple-800",
  Financial: "bg-yellow-100 text-yellow-800",
  Spiritual: "bg-indigo-100 text-indigo-800",
  Provision: "bg-emerald-100 text-emerald-800",
  Guidance: "bg-sky-100 text-sky-800",
  Thanksgiving: "bg-orange-100 text-orange-800",
  Other: "bg-gray-100 text-gray-800",
};

/* ---- counts normalizer ---- */
function getCount(obj, key) {
  if (!obj) return 0;
  if (typeof obj[`${key}Count`] === "number") return obj[`${key}Count`];
  if (obj._count && typeof obj._count[key] === "number") return obj._count[key];
  if (obj.stats && typeof obj.stats[key] === "number") return obj.stats[key];
  if (obj.meta && typeof obj.meta[key] === "number") return obj.meta[key];
  if (Array.isArray(obj[key])) return obj[key].length;
  return 0;
}

/* ---------------- identity hydration (CuratedList-style, but wider) ---------------- */
function idOf(u) {
  if (!u) return null;
  if (typeof u === "string") return u;
  return u.id || u.userId || u.ownerId || u.createdById || null;
}

function collectIdsFromRows(rows) {
  const ids = new Set();
  for (const r of rows) {
    // common shapes
    [r.user, r.owner, r.createdBy].forEach((x) => { const id = idOf(x); if (id) ids.add(id); });
    [r.userId, r.ownerId, r.createdById].forEach((id) => { if (id) ids.add(id); });
  }
  return Array.from(ids).filter(Boolean);
}

function collectIdsFromComments(comments) {
  const ids = new Set();
  for (const c of comments || []) {
    const id = idOf(c.user) || c.userId || null;
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

// Accepts shapes:
// 1) { data: [{id,displayName,...}] }  2) bare array  3) { map: { [id]: {id,displayName,...} } }
async function lookupUsersByIds(ids) {
  if (!ids?.length) return new Map();
  const qs = `?ids=${encodeURIComponent(ids.join(","))}`;
  const candidates = [
    `/identity/lookup${qs}`,
    `/users/lookup${qs}`,
    `/admin/users/lookup${qs}`, // may 401/403; we soft-fail
  ];
  for (const path of candidates) {
    try {
      const res = await request(path, { allow401: true });
      if (!res) continue;

      // if it is { map: { id: user } }
      if (res?.map && typeof res.map === "object") {
        const out = new Map();
        for (const [k, v] of Object.entries(res.map)) out.set(k, v);
        if (out.size) return out;
      }

      // if array or { data: array }
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : null);
      if (arr) {
        const out = new Map();
        for (const u of arr) if (u?.id) out.set(u.id, u);
        if (out.size) return out;
      }
    } catch {}
  }
  return new Map();
}

// Build display name with strict no-email fallback
function displayNameFromMaps(req, userMap) {
  if (req.anonymous) return "Anonymous";

  // 1) Embedded objects (avoid email)
  const fromEmbedded =
    (req.user && (req.user.displayName || req.user.name)) ||
    (req.owner && (req.owner.displayName || req.owner.name)) ||
    (req.createdBy && (req.createdBy.displayName || req.createdBy.name));
  if (fromEmbedded) return fromEmbedded;

  // 2) Hydrated ids
  const idCandidates = [req.userId, req.ownerId, req.createdById].filter(Boolean);
  for (const id of idCandidates) {
    const u = userMap.get(id);
    const nm = u?.displayName || u?.name || null;
    if (nm) return nm;
  }

  // 3) Server provided strings (never email)
  if (req.ownerDisplayName) return req.ownerDisplayName;
  if (req.createdByName) return req.createdByName;

  return "User";
}

const PrayerWalls = () => {
  const nav = useNavigate();

  const [prayerRequests, setPrayerRequests] = useState([]);
  const [userMap, setUserMap] = useState(() => new Map()); // id -> {id, displayName, ...}
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showComments, setShowComments] = useState(null);
  const [commentsMap, setCommentsMap] = useState({});
  const [newComment, setNewComment] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("newest");

  const [formData, setFormData] = useState({
    title: "", content: "", category: "Other", isUrgent: false, isAnonymous: false, book: "", chapter: "", verse: "",
  });

  const [toastMessage, setToastMessage] = useState("");
  const showToast = (m) => { setToastMessage(m); setTimeout(() => setToastMessage(""), 3000); };

  // current user id (for "You commented")
  const [meId, setMeId] = useState(null);
  useEffect(() => {
    (async () => {
      const me = await request("/auth/me", { allow401: true }).catch(() => null);
      const u = me?.user ?? me ?? null;
      setMeId(u?.id || null);
    })();
  }, []);

  usePageLogger({ title: "Prayer Wall", type: "page" });

  /* ---------------- Load list + hydrate names ---------------- */
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const usp = new URLSearchParams();
      if (searchTerm.trim()) usp.set("q", searchTerm.trim());
      if (selectedCategory !== "All") usp.set("category", selectedCategory);
      // Ask API to include 'user' when possible (helps show names immediately)
      usp.set("include", "user,counts");

      const res = await request(`/prayer-wall${usp.toString() ? `?${usp.toString()}` : ""}`);
      const rows = res?.data ?? res ?? [];

      const norm = rows.map((r) => ({
        ...r,
        _likesCount: getCount(r, "likes"),
        _commentsCount: getCount(r, "comments"),
        currentUserLiked: !!r.currentUserLiked,
        currentUserBookmarked: !!r.currentUserBookmarked,
        currentUserCommented: !!r.currentUserCommented,
      }));
      setPrayerRequests(norm);

      // Hydrate any missing identities across all visible rows
      const ids = collectIdsFromRows(norm).filter((id) => !userMap.has(id));
      if (ids.length) {
        const fetched = await lookupUsersByIds(ids);
        if (fetched.size) {
          setUserMap((prev) => {
            const next = new Map(prev);
            fetched.forEach((v, k) => next.set(k, v));
            return next;
          });
        }
      }
    } catch (err) {
      if (err.status === 401) nav("/login", { replace: true });
      else showToast(err.message || "Failed to load prayer wall");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, nav, userMap]);

  useEffect(() => { loadList(); }, [loadList]);

  // total users (optional)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await request("/stats/users-count", { allow401: true });
        if (alive && res) setTotalUsers(Number(res?.count ?? 0));
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  /* ---------------- Stats widget (optional HEAD) ---------------- */
  const statsWarnedRef = useRef(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let requests = 0;
        try {
          const head = await fetch(apiURL("/prayer-wall"), { method: "HEAD", credentials: "include", cache: "no-store" });
          if (head.ok) {
            const hdr = head.headers.get("x-total-count");
            const n = Number(hdr);
            if (Number.isFinite(n) && n >= 0) requests = n;
          }
        } catch {}
        if (!requests) requests = Array.isArray(prayerRequests) ? prayerRequests.length : 0;
        if (!alive) return;
        if (!statsWarnedRef.current && requests === 0) {
          console.warn("PrayerWalls: fallback list-length for requests; add HEAD /prayer-wall with X-Total-Count for accuracy.");
          statsWarnedRef.current = true;
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [prayerRequests]);

  /* ---------------- Sorting ---------------- */
  const filteredSorted = (() => {
    let arr = [...prayerRequests];
    switch (sortBy) {
      case "newest":     arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); break;
    } switch (sortBy) {
      case "oldest":     arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); break;
      case "most-prayed":arr.sort((a, b) => (b._likesCount || 0) - (a._likesCount || 0)); break;
      case "urgent":     arr.sort((a, b) => Number(b.urgent) - Number(a.urgent)); break;
      default: break;
    }
    return arr;
  })();

  /* ---------------- Actions ---------------- */
  async function onToggleLike(id) {
    const current = prayerRequests.find((r) => r.id === id);
    if (!current) return;
    setPrayerRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, currentUserLiked: !r.currentUserLiked, _likesCount: (r._likesCount || 0) + (!r.currentUserLiked ? 1 : -1) }
          : r
      )
    );
    try {
      await request(`/prayer-wall/${id}/like`, { method: "POST" });
    } catch (err) {
      // revert
      setPrayerRequests((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, currentUserLiked: !r.currentUserLiked, _likesCount: (r._likesCount || 0) + (r.currentUserLiked ? 1 : -1) }
            : r
        )
      );
      if (err.status === 401) nav("/login", { replace: true });
      else showToast(err.message || "Action failed");
    }
  }

  async function onToggleBookmark(id) {
    const current = prayerRequests.find((r) => r.id === id);
    if (!current) return;
    setPrayerRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, currentUserBookmarked: !r.currentUserBookmarked } : r))
    );
    try {
      await request(`/prayer-wall/${id}/bookmark`, { method: "POST" });
      showToast(current.currentUserBookmarked ? "Removed bookmark" : "Bookmarked!");
    } catch (err) {
      // revert
      setPrayerRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, currentUserBookmarked: !r.currentUserBookmarked } : r))
      );
      if (err.status === 401) nav("/login", { replace: true });
      else showToast(err.message || "Action failed");
    }
  }

  async function onToggleComments(id) {
    const next = showComments === id ? null : id;
    setShowComments(next);
    if (!next) return;

    try {
      const res = await request(`/prayer-wall/${id}?include=comments,counts,user,owner,createdBy,current`);
      const item = res?.data ?? res ?? null;
      const comments = item?.comments ?? [];

      // hydrate commenters too
      const missing = collectIdsFromComments(comments).filter((uid) => !userMap.has(uid));
      if (missing.length) {
        const fetched = await lookupUsersByIds(missing);
        if (fetched.size) {
          setUserMap((prev) => {
            const nextMap = new Map(prev);
            fetched.forEach((v, k) => nextMap.set(k, v));
            return nextMap;
          });
        }
      }

      setCommentsMap((m) => ({ ...m, [id]: comments }));

      const youCommented =
        !!item?.currentUserCommented ||
        (!!meId && comments.some((c) => (c?.user?.id || c?.userId || null) === meId));

      setPrayerRequests((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                _commentsCount: getCount(item, "comments") || comments.length || r._commentsCount || 0,
                _likesCount: getCount(item, "likes") || r._likesCount || 0,
                currentUserBookmarked: !!item?.currentUserBookmarked,
                currentUserLiked: !!item?.currentUserLiked,
                currentUserCommented: youCommented,
                // attach objects for better embedded name resolution next render
                user: item?.user || r.user,
                owner: item?.owner || r.owner,
                createdBy: item?.createdBy || r.createdBy,
                userId: item?.userId ?? r?.userId,
                ownerId: item?.ownerId ?? r?.ownerId,
                createdById: item?.createdById ?? r?.createdById,
              }
            : r
        )
      );
    } catch (err) {
      if (err.status === 401) nav("/login", { replace: true });
      else showToast(err.message || "Failed to load comments");
    }
  }

  async function onAddComment(requestId) {
    const body = newComment.trim();
    if (!body) return;
    try {
      const res = await request(`/prayer-wall/${requestId}/comments`, { method: "POST", body: { body } });
      const created = res?.data ?? res;

      // hydrate the comment author (me) if needed
      const myId = created?.user?.id || created?.userId || meId;
      if (myId && !userMap.has(myId)) {
        const fetched = await lookupUsersByIds([myId]);
        if (fetched.size) {
          setUserMap((prev) => {
            const next = new Map(prev);
            fetched.forEach((v, k) => next.set(k, v));
            return next;
          });
        }
      }

      setCommentsMap((m) => ({ ...m, [requestId]: [created, ...(m[requestId] || [])] }));
      setPrayerRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? { ...r, _commentsCount: (r._commentsCount || 0) + 1, currentUserCommented: true }
            : r
        )
      );
      setNewComment("");
      showToast("Comment added");
    } catch (err) {
      if (err.status === 401) nav("/login", { replace: true });
      else showToast(err.message || "Failed to add comment");
    }
  }

  function resetForm() {
    setFormData({ title: "", content: "", category: "Other", isUrgent: false, isAnonymous: false, book: "", chapter: "", verse: "" });
  }
  async function onCreate(e) {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.content.trim(),
        category: formData.category || "Other",
        urgent: !!formData.isUrgent,
        anonymous: !!formData.isAnonymous,
      };
      if (formData.book && Number(formData.chapter) && Number(formData.verse)) {
        payload.book = formData.book.trim();
        payload.chapter = Number(formData.chapter);
        payload.verse = Number(formData.verse);
      }
      await request("/prayer-wall", { method: "POST", body: payload });
      setShowModal(false);
      resetForm();
      showToast("Prayer request posted successfully!");
      loadList();
      logPrayer(`Posted Prayer Request: ${payload.title}`, payload.description, payload.category);
    } catch (err) {
      if (err.status === 401) nav("/login", { replace: true });
      else showToast(err.message || "Create failed");
    }
  }

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const now = new Date();
    const date = new Date(dateString);
    const diffH = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffH < 1) return "Just now";
    if (diffH < 24) return `${diffH} hours ago`;
    return `${Math.floor(diffH / 24)} days ago`;
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 pt-24 pl-0 lg:pl-[224px] font-['Poppins']">
      {toastMessage && <Toast message={toastMessage} />}

      <main className="flex-1 space-y-10 px-4 lg:px-6 pb-10">
        <div className="container mx-auto px-4 py-8 space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0C2E8A] to-blue-700 rounded-lg shadow-lg p-6 md:p-8 text-white mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-base font-semibold mb-2">Community Prayer Wall</h1>
                <p className="text-blue-100 text-sm">
                  {totalUsers == null
                    ? "Join believers in prayer and support"
                    : <>Join <span className="font-semibold">{totalUsers.toLocaleString()}</span>+ believers in prayer and support</>}
                </p>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                  <p className="text-sm font-medium">{prayerRequests.length} Active Requests</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action card */}
          <div className="bg-[#FCCF3A] rounded-lg p-4 mt-6">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-[#0C2E8A] font-semibold text-base mb-1">Need Prayer?</h3>
                <p className="text-gray-600 text-sm mb-3">Share your prayer request with our loving community</p>
                <button
                  className="bg-[#0C2E8A] text-white px-6 py-2.5 rounded-lg hover:bg-blue-800 transition font-medium text-sm flex items-center gap-2"
                  onClick={() => setShowModal(true)}
                >
                  <Plus className="w-4 h-4" />
                  Post Your Prayer Request
                </button>
              </div>
            </div>
          </div>

          {/* Browse header */}
          <div className="flex items-center pt-8 justify-between gap-3 mb-4">
            <div className="grid">
              <h2 className="text-base font-semibold text-[#0C2E8A]">Browse Prayer Requests</h2>
              <p className="text-sm text-gray-600">Support others through prayer and encouragement</p>
            </div>
            <div className="w-10 h-0.5 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full"></div>
          </div>

          {/* Search */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search prayer requests by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadList()}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-[#0C2E8A] focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 md:p-6">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
              <div className="flex-1 w-full">
                <label className="block text-sm font-medium text-[#0C2E8A] mb-2">Filter by Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                        selectedCategory === cat
                          ? "bg-[#0C2E8A] text-white"
                          : "bg-[#FCCF3A] text-[#0C2E8A] font-bold hover:bg-[#ABBC6B]"
                      }`}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort by</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0C2E8A] focus:border-transparent"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="most-prayed">Most Liked</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <button onClick={loadList} className="px-4 py-2 rounded-md border bg-white text-gray-900">
                Apply
              </button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-4">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-40 bg-white rounded-lg shadow-sm border border-gray-100 animate-pulse" />
              ))
            ) : filteredSorted.length > 0 ? (
              filteredSorted.map((req) => {
                const name = displayNameFromMaps(req, userMap);
                const created = req.createdAt ? formatTimeAgo(req.createdAt) : "";
                const commentsFor = commentsMap[req.id] || [];

                const youBadges = [];
                if (req.currentUserLiked) youBadges.push("You liked this");
                if (req.currentUserCommented) youBadges.push("You commented");

                return (
                  <div
                    key={req.id}
                    className={`bg-white rounded-lg shadow-sm border border-gray-100 p-4 md:p-6 hover:shadow-lg transition ${
                      req.answered ? "bg-green-50 border-green-300" : ""
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0C2E8A] text-white font-bold flex-shrink-0">
                          {name?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-[#0C2E8A] break-words">{req.title}</h3>
                          <p className="text-xs text-gray-500">by {name} {created && `• ${created}`}</p>
                        </div>
                      </div>

                      {/* Bookmark */}
                      <button
                        onClick={() => onToggleBookmark(req.id)}
                        className={`p-2 rounded-lg transition flex-shrink-0 ${
                          req.currentUserBookmarked
                            ? "bg-blue-50 text-blue-700"
                            : "bg-gray-100 text-gray-600 hover:bg-yellow-100 hover:text-yellow-600"
                        }`}
                        title="Bookmark"
                      >
                        <Bookmark className={`w-5 h-5 ${req.currentUserBookmarked ? "fill-current" : ""}`} />
                      </button>
                    </div>

                    {youBadges.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {youBadges.map((t) => (
                          <span key={t} className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    {req.description && (
                      <p className="text-gray-700 text-sm mb-4 break-words leading-relaxed">{req.description}</p>
                    )}

                    <div className="flex gap-2 flex-wrap mb-4">
                      <span
                        className={`px-3 py-1 text-xs rounded-full font-medium ${
                          categoryColors[req.category] || categoryColors.Other
                        }`}
                      >
                        {req.category || "Other"}
                      </span>
                      {req.urgent && (
                        <span className="px-3 py-1 text-xs rounded-full bg-red-200 text-red-800 font-medium">
                          Urgent
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-4">
                        <button
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition font-medium text-sm ${
                            req.currentUserLiked ? "bg-red-50 text-red-600" : "bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-600"
                          }`}
                          onClick={() => onToggleLike(req.id)}
                          title={req.currentUserLiked ? "Unlike" : "Like"}
                        >
                          <Heart className={`w-4 h-4 ${req.currentUserLiked ? "fill-current" : ""}`} />
                          <span>{req._likesCount || 0}</span>
                        </button>

                        <button
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-blue-50 hover:text-[#0C2E8A] transition font-medium text-sm"
                          onClick={() => onToggleComments(req.id)}
                          title="Comments"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>{req._commentsCount ?? commentsFor.length ?? 0}</span>
                        </button>
                      </div>
                    </div>

                    {/* Comments */}
                    {showComments === req.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                        {commentsFor.length === 0 ? (
                          <div className="text-sm text-gray-500">No comments yet.</div>
                        ) : (
                          commentsFor.map((c) => {
                            const uid = idOf(c.user) || c.userId || null;
                            const hydrated = uid ? userMap.get(uid) : null;
                            const who =
                              (c.user && (c.user.displayName || c.user.name)) ||
                              (hydrated && (hydrated.displayName || hydrated.name)) ||
                              "User";
                            const when = c.createdAt ? new Date(c.createdAt).toLocaleString() : "";
                            return (
                              <div key={c.id} className="flex gap-3 items-start bg-gray-50 p-3 rounded-lg">
                                <div className="w-8 h-8 rounded-full bg-[#0C2E8A] flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                                  {who?.[0]?.toUpperCase() || "U"}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#0C2E8A] break-words">{who}</p>
                                  <p className="text-sm text-gray-700 break-words">{c.body}</p>
                                  {when && <span className="text-xs text-gray-400">{when}</span>}
                                </div>
                              </div>
                            );
                          })
                        )}

                        {/* Add Comment */}
                        <div className="flex gap-2 items-center mt-3">
                          <input
                            type="text"
                            placeholder="Add a comment..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && onAddComment(req.id)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0C2E8A] focus:border-transparent text-sm"
                          />
                          <button
                            onClick={() => onAddComment(req.id)}
                            className="bg-[#0C2E8A] text-white p-2 rounded-lg hover:bg-blue-800 transition flex-shrink-0"
                            title="Send comment"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-10 h-10 text-blue-700" />
                </div>
                <h3 className="text-base font-medium text-gray-900 mb-2">
                  {searchTerm || selectedCategory !== "All"
                    ? "No prayer requests match your search"
                    : "No prayer requests yet"}
                </h3>
                <p className="text-gray-600 mb-6 px-4">
                  {searchTerm || selectedCategory !== "All"
                    ? "Try adjusting your search or filter"
                    : "Be the first to share a prayer request with the community"}
                </p>
                {!searchTerm && selectedCategory === "All" && (
                  <button
                    onClick={() => setShowModal(true)}
                    className="bg-[#0C2E8A] text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition font-medium"
                  >
                    Add First Prayer Request
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Create Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* Modal header */}
              <div className="bg-gradient-to-r from-[#0C2E8A] to-blue-700 p-6 rounded-t-2xl">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-4">
                    <div>
                      <h2 className="text-xl font-bold text-white mb-1">Share Your Prayer Request</h2>
                      <p className="text-blue-100 text-sm">Our community is here to pray with you</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="p-2 hover:bg-white/20 rounded-lg transition text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={onCreate} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Prayer Request Title *</label>
                  <input
                    type="text"
                    placeholder="e.g., Healing for my mother, Job interview tomorrow..."
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0C2E8A] focus:border-[#0C2E8A] transition"
                    required maxLength={100}
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.title.length}/100 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Describe Your Request *</label>
                  <textarea
                    placeholder="Share the details of what you need prayer for…"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows="5"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0C2E8A] focus:border-[#0C2E8A] resize-none transition"
                    required maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.content.length}/500 characters</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0C2E8A] focus:border-[#0C2E8A] transition"
                    >
                      {categories.filter((c) => c !== "All").map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3 items-center">
                    <label className="inline-flex items-center gap-2 mt-7">
                      <input
                        type="checkbox"
                        checked={formData.isUrgent}
                        onChange={(e) => setFormData({ ...formData, isUrgent: e.target.checked })}
                      />
                      <span className="text-sm">Mark as urgent</span>
                    </label>
                    <label className="inline-flex items-center gap-2 mt-7">
                      <input
                        type="checkbox"
                        checked={formData.isAnonymous}
                        onChange={(e) => setFormData({ ...formData, isAnonymous: e.target.checked })}
                      />
                      <span className="text-sm">Post anonymously</span>
                    </label>
                  </div>
                </div>

                {/* Optional verse reference */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Book (optional)</label>
                    <input
                      value={formData.book}
                      onChange={(e) => setFormData({ ...formData, book: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                      placeholder="e.g., John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Chapter</label>
                    <input
                      type="number" min="1"
                      value={formData.chapter}
                      onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Verse</label>
                    <input
                      type="number" min="1"
                      value={formData.verse}
                      onChange={(e) => setFormData({ ...formData, verse: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-[#0C2E8A] text-white rounded-lg hover:bg-blue-800 transition font-medium shadow-lg shadow-blue-500/30"
                  >
                    Submit Prayer Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @keyframes slide-in { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default PrayerWalls;
